import cn from 'classnames';
import { FormikValues } from 'formik';
import { useCallback, useEffect, useState } from 'react';

import { useVerificationCodeByEmail } from '@api/auth';
import { useRegisterCustomer } from '@api/customers';

import Form from '@controls/Form';

import { CodeInput } from '@components/CodeInput';
import FormMessage from '@components/controls/Form/Message';
import Button from '@components/uiKit/Button';
import { LoadingSpinner } from '@components/uiKit/LoadingSpinner';

import { DEFAULT_TIMEZONE } from '@scripts/constants';
import { CustomersTypeEnum } from '@scripts/enums';

import { FormSubmitBinder } from './FormSubmitBinder';
import styles from './SendCode.module.scss';
import { SendCodeByEmailProps } from './types';

const SendCode = ({
    logIn,
    backToSignin,
    phone,
    email,
    errorMessage,
    registerInfo,
    isLogin,
    setErrorMessage,
    setEmail,
    sendCodeByPhoneForRegistration,
    startTimer,
    stopTimer,
    timeLeft,
    isCanSendAgain,
    register,
    hidePrimaryActions = false,
    bindSubmit,
}: SendCodeByEmailProps) => {
    const { mutateAsync: verificationCodeByEmail } = useVerificationCodeByEmail();

    const [isLoading, setLoading] = useState(false);
    const [code, setCode] = useState('');

    const { error: errorRegisterCustomer } = useRegisterCustomer();

    const loginByEmail = async () => {
        await logIn({ email: email || '', code: code });
    };

    const handlerSubmit = async () => {
        await verificationCodeByEmail({ email: email || '' });

        setErrorMessage('');
        setCode('');
        startTimer?.();
    };

    const handleSwitchToPhoneLogin = useCallback(() => {
        setErrorMessage('');

        if (isLogin) {
            backToSignin();
        } else {
            sendCodeByPhoneForRegistration?.(phone || '');
        }
    }, [isLogin, backToSignin, setErrorMessage, sendCodeByPhoneForRegistration, phone]);

    useEffect(() => {
        if (code && code.length === 4 && isLogin) {
            setLoading(prev => !prev);
            loginByEmail()
                .catch(error => {
                    setErrorMessage(error.message);
                })
                .finally(() => {
                    setLoading(false);
                    stopTimer?.();
                });
        }
        if (code && code.length === 4 && registerInfo && registerInfo.phone && !isLogin) {
            setLoading(prev => !prev);
            try {
                if (register) {
                    register({
                        ...(registerInfo.inn && {
                            ...registerInfo.inn,
                            inn: registerInfo.inn.value,
                        }),
                        type:
                            registerInfo?.inn?.value?.length === 10
                                ? CustomersTypeEnum.ENTITY
                                : registerInfo?.inn?.value?.length === 12
                                  ? CustomersTypeEnum.IP
                                  : CustomersTypeEnum.PHYSICAL,
                        code: code,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE,
                        phone: phone,
                        email: email,
                    });
                }
            } catch (error: any) {
                setErrorMessage(error.message);
            } finally {
                setLoading(false);
                stopTimer?.();
            }
        }
    }, [code, isLogin, registerInfo, phone, email, register, stopTimer]);

    return (
        <Form onSubmit={handlerSubmit} initialValues={{ code: '' }}>
            {() => (
                <div className={styles.root}>
                    {bindSubmit && <FormSubmitBinder onBind={bindSubmit} />}
                    <div className={styles.hint}>
                        Отправили на почту <span className={styles.contact}>{email}</span>
                        <span
                            onClick={() => {
                                backToSignin();
                                setErrorMessage('');
                                setEmail('');
                            }}
                            className={styles.change}
                        >
                            Изменить
                        </span>
                    </div>
                    <div className={styles.code}>
                        {isLoading ? <LoadingSpinner embedded noPadding /> : <CodeInput onChange={setCode} />}
                    </div>
                    {(errorMessage || errorRegisterCustomer?.message) && (
                        <FormMessage
                            type="error"
                            message={errorMessage || errorRegisterCustomer?.message || ''}
                            className={styles.error}
                        />
                    )}

                    {!isCanSendAgain && (
                        <div className={styles.timer}>
                            <div className={styles.timerText}>
                                Если код не придёт, можно получить новый через {timeLeft ?? 0} сек. или{' '}
                                {isLogin ? ' войти ' : ' подтвердить '}
                                <span onClick={handleSwitchToPhoneLogin} className={styles.link}>
                                    по номеру телефона
                                </span>
                            </div>
                        </div>
                    )}

                    {isCanSendAgain && (
                        <div>
                            {!errorMessage && (
                                <div className={cn(styles.hintBox, styles.hintBoxWide)}>
                                    <span className={cn(styles.hintBoxText, isLogin && styles.hintBoxText_login)}>
                                        Не пришел код? Запросите новый или {isLogin ? 'войдите ' : 'подтвердите '}
                                        по номеру телефона
                                    </span>
                                </div>
                            )}
                            {!hidePrimaryActions && (
                                <Button
                                    type="submit"
                                    variant="mainOrange"
                                    fullWidth
                                    data-testid="button-submit"
                                    role="button"
                                    className={styles.resend}
                                >
                                    Получить новый код
                                </Button>
                            )}

                            {!hidePrimaryActions && (
                                <Button
                                    type="button"
                                    variant="reserveWhite"
                                    fullWidth
                                    onClick={handleSwitchToPhoneLogin}
                                    className={styles.altButton}
                                >
                                    {isLogin ? 'Войти по номеру' : 'Подтвердить по номеру'}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Form>
    );
};

export default SendCode;
